/* eslint-disable jsx-a11y/anchor-is-valid */
import './App.css';
import { Md, LoadingSpinner, EmptyState, DataTable, addYearsMonths, formatMonthYearIT } from './utils.js';
import { DETAIL_TABLE_CONFIG } from './config.js';
import React, { useMemo, useEffect, useState } from 'react';

// ---------------------------------------------------------------------------
// Main App
// ---------------------------------------------------------------------------

function App({ config }) {
  const [anno, setAnno]         = useState('');
  const [mese, setMese]         = useState('');
  const [codSerie, setCodSerie] = useState('');

  const [view, setView] = useState('serie');

  const [serieRows, setSerieRows]     = useState([]);
  const [hasSearched, setHasSearched] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);
  const [detail, setDetail]           = useState(null);
  const [error, setError]             = useState('');
  const [loadingSerie, setLoadingSerie]   = useState(false);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const scrollToFilters = () => {
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        document.getElementById('buoni-postali-result-filters')?.scrollIntoView({
          behavior: 'smooth',
          block: 'start',
        });
      });
    });

  };

  const codSctgUni = useMemo(() => {
    const params = new URLSearchParams(window.location.search);
    return params.get('code') || '';
  }, []);

  const apiBaseUrl = useMemo(() => {
    const { systemParams } = config || {};
    const { api } = systemParams || {};
    return api && api['risparmiatori-api']?.url;
  }, [config]);

  const serieUrl = apiBaseUrl ? `${window.location.origin}${apiBaseUrl}/api/rendimenti/serie` : null;
  const allUrl   = apiBaseUrl ? `${window.location.origin}${apiBaseUrl}/api/rendimenti/all`   : null;

  const missingCodSctgUni = !codSctgUni;

  const normalizeDateForApi = (value) => {
    if (!value) return '';
    const parts = value.split('/');
    if (parts.length !== 3) return value;
    const [dd, mm, yyyy] = parts;
    return `${yyyy}-${mm.padStart(2, '0')}-${dd.padStart(2, '0')}`;
  };

  const resolveSubtitle = (subtitle, rows) =>
      typeof subtitle === 'function' ? subtitle(rows) : subtitle;

  const resolveConfig = (det) => {
    const type = det?.type || 'default';
    return DETAIL_TABLE_CONFIG[type] || DETAIL_TABLE_CONFIG.default;
  };

  const buildHeaders = (cfg, rows) => {
    if (cfg.headers) return cfg.headers;
    if (cfg.headersFromRowLength && rows?.[0]) {
      return rows[0].map((_, idx) => `Colonna ${idx + 1}`);
    }
    return [];
  };

  useEffect(() => {
    if (!codSctgUni) return;
    if (view !== 'serie') return;
    scrollToFilters();

  }, [codSctgUni, view]);

  const handleSearch = async (e) => {
    e.preventDefault();
    if (!apiBaseUrl) { setError('API non configurata'); return; }

    setError('');
    setDetail(null);
    setSelectedRow(null);
    setView('serie');
    setLoadingSerie(true);

    try {
      const url = new URL(serieUrl);
      url.searchParams.set('codSctgUni', codSctgUni);
      if (anno)     url.searchParams.set('anno', anno);
      if (mese)     url.searchParams.set('mese', mese);
      if (codSerie) url.searchParams.set('codSerie', codSerie);

      const response = await fetch(url.toString());
      if (!response.ok) {
        setError('Errore nella chiamata a /serie');
        setSerieRows([]);
        return;
      }

      const payload = await response.json();
      setSerieRows(Array.isArray(payload) ? payload : []);
    } catch (err) {
      setError(err.message || 'Errore imprevisto');
      setSerieRows([]);
    } finally {
      setHasSearched(true);
      setLoadingSerie(false);
    }
  };

  const handleRowClick = async (row) => {
    if (!apiBaseUrl) { setError('API non configurata'); return; }

    setError('');
    setLoadingDetail(true);
    setSelectedRow(row);
    setView('detail');

    try {
      const url = new URL(allUrl);
      url.searchParams.set('codSctgUni', codSctgUni);
      url.searchParams.set('codSerie', row.codSerie);
      url.searchParams.set('emessoDal', normalizeDateForApi(row.emessoDal));
      url.searchParams.set('emessoAl',  normalizeDateForApi(row.emessoAl));

      const response = await fetch(url.toString());
      if (!response.ok) {
        setError('Errore nella chiamata a /all');
        setLoadingDetail(false);
        return;
      }

      const payload = await response.json();
      setDetail(payload);
      scrollToFilters();
    } catch (err) {
      setError(err.message || 'Errore imprevisto');
    } finally {
      setLoadingDetail(false);
    }
  };

  const handleBackToSerie = () => {
    setView('serie');
    setDetail(null);
    setSelectedRow(null);
    setError('');
  };

  const renderDetailView = () => {
    scrollToFilters();
    if (!detail || !selectedRow) return (
        <div className="buoni-postali__container" id="buoni-postali-result-filters" style={{ scrollMarginTop: '100px' }}>
          <div className="buoni-postali__filters"></div>
          <div className="buoni-postali__buttons">
            <button className="btn btn-primary" onClick={handleBackToSerie}>← Torna ai risultati</button>
          </div>
        </div>
    );

    const cfg  = resolveConfig(detail);
    const rows = Array.isArray(detail.data) ? detail.data : [];

    const secondaryRows = Array.isArray(detail.otherData) ? detail.otherData : null;
    const secondaryCfg  = cfg.secondary;
    const templateData = {
      DATA_PLUS_4: formatMonthYearIT(
          addYearsMonths(selectedRow.emessoAl, 4, 11)
      ),
    };

    return (
        <>
          <div className="buoni-postali__container" id="buoni-postali-result-filters" style={{ scrollMarginTop: '100px' }}>
            <div className="buoni-postali__filters"></div>
            <div className="buoni-postali__buttons">
              <button className="btn btn-primary" onClick={handleBackToSerie}>← Torna ai risultati</button>
            </div>
          </div>

          {/* Centered title block */}
          <div className="detail-title text-center font-weight-600">
            <p className="detail-title__serie">
              Serie {selectedRow.codSerie} – Sottoscrizioni dal {selectedRow.emessoDal} al {selectedRow.emessoAl}
            </p>
            {cfg.subtitle && (
                <p className="detail-title__subtitle">
                  {resolveSubtitle(cfg.subtitle, rows)}
                </p>
            )}
          </div>

          {/* Text above */}
          {cfg.textAbove && <Md className="table-text-above">{cfg.textAbove}</Md>}

          {/* Main table */}
          {rows.length === 0 ? (
              <EmptyState message="Nessun dato disponibile per la serie selezionata."/>
          ) : cfg.renderTable ? (
              cfg.renderTable(rows, { templateData })
          ) : (
              <DataTable
                  headers={buildHeaders(cfg, rows)}
                  rows={rows}
                  formatCell={cfg.formatCell}
              />
          )}


          {/* Secondary table */}
          {secondaryCfg && secondaryRows && secondaryRows.length > 0 && (
              <div className="secondary-table">
                {secondaryCfg.textAbove && <Md className="table-text-above">{secondaryCfg.textAbove}</Md>}
                {secondaryCfg.renderTable ? (
                    secondaryCfg.renderTable(secondaryRows, { templateData })
                ) : (
                    <DataTable
                        headers={buildHeaders(secondaryCfg, secondaryRows)}
                        rows={secondaryRows}
                        formatCell={secondaryCfg.formatCell}
                    />
                )}
                {secondaryCfg.textBelow && <Md className="table-text-below">{secondaryCfg.textBelow}</Md>}
              </div>
          )}

          {/* Fallback for non-array otherData */}
          {detail.otherData && !Array.isArray(detail.otherData) && (
              <div style={{marginTop: 16}}>
                <h4>Altri dati</h4>
                <pre style={{background: '#f5f5f5', padding: 12, overflow: 'auto'}}>
              {JSON.stringify(detail.otherData, null, 2)}
            </pre>
              </div>
          )}
        </>
    );
  };

  // Missing code: show only error
  if (missingCodSctgUni) {
    return (
        <section className="section-contenuto-rte contenuto-rte-center">
          <div className="container">
            <div className="row"></div>
          </div>
        </section>
    );
  }

  return (
      <section className="buoni-postali my-3 my-lg-5">
        <div className="container">
          <div className="row">
            <div className="col-12">

              {view === 'serie' && (
                  <div className="buoni-postali__container" id="buoni-postali-result-filters" style={{ scrollMarginTop: '100px' }}>
                    <div className="buoni-postali__filters">

                      {/* Anno */}
                      <div className="buoni-postali__filter">
                        <label className="form-label active" htmlFor="anno">Anno</label>
                        <input
                            type="number"
                            id="anno"
                            className="form-control"
                            value={anno}
                            onChange={(e) => setAnno(e.target.value)}
                            placeholder="es. 2024"
                            autoComplete="off"
                        />
                      </div>

                      {/* Mese */}
                      <div className="buoni-postali__filter">
                        <label className="form-label active" htmlFor="mese">Mese</label>
                        <input
                            type="number"
                            id="mese"
                            className="form-control"
                            min="1"
                            max="12"
                            value={mese}
                            onChange={(e) => setMese(e.target.value)}
                            placeholder="1–12"
                            autoComplete="off"
                        />
                      </div>

                      {/* Codice Serie */}
                      <div className="buoni-postali__filter">
                        <label className="form-label active" htmlFor="codSerie">Codice Serie</label>
                        <input
                            type="text"
                            id="codSerie"
                            className="form-control"
                            value={codSerie}
                            onChange={(e) => setCodSerie(e.target.value)}
                            placeholder="codSerie"
                            autoComplete="off"
                        />
                      </div>

                    </div>

                    <div className="buoni-postali__buttons">
                      <button
                          type="submit"
                          data-text="Cerca"
                          data-event="button_click"
                          className={`datalayer-btn btn btn-primary${loadingSerie ? ' disabled' : ''}`}
                          disabled={loadingSerie}
                          onClick={handleSearch}
                      >
                        {loadingSerie ? 'Caricamento...' : 'Cerca'}
                      </button>
                    </div>
                  </div>
              )}

              {error && (
                  <div className="error-banner" style={{ marginBottom: 16 }}>{error}</div>
              )}


              {/* SERIE VIEW */}
              {view === 'serie' && (
                  <>
                    {/* Loading */}
                    {loadingSerie && <LoadingSpinner />}

                    {/* Results — only shown after a search has completed */}
                    {!loadingSerie && hasSearched && !error && (
                        serieRows.length === 0 ? (
                            <EmptyState message="Nessun risultato trovato per i criteri inseriti." />
                        ) : (
                            <>
                              {loadingDetail && <LoadingSpinner />}
                              <section className="obbligazioni-table">
                                <div className="obbligazioni-table__wrapper">
                                  <table className="obbligazioni-table__table" aria-label="Emissioni programma DIP">
                                    <thead>
                                    <tr>
                                      <th scope="col" colSpan={3}><div className="obbligazioni-table__header">Risultato</div></th>
                                    </tr>
                                    </thead>
                                    <tbody>
                                    {serieRows.map((row, idx) => (
                                        <tr
                                            key={`${row.codSerie}-${row.emessoDal}-${idx}`}
                                            onClick={() => !loadingDetail && handleRowClick(row)}
                                            style={{ cursor: loadingDetail ? 'wait' : 'pointer' }}
                                        >
                                          <td>{row.codSerie}</td>
                                          <td>{row.emessoDal}</td>
                                          <td>{row.emessoAl}</td>
                                        </tr>
                                    ))}
                                    </tbody>
                                  </table>
                                  <div class="obbligazioni-table__mobile-cards">
                                    {serieRows.map((row, idx) => (
                                        <div class="obbligazioni-table__mobile-card">
                                          <div class="obbligazioni-table__mobile-card-header">
                                            <span class="obbligazioni-table__mobile-card-label">Risultato</span>
                                          </div>
                                          <div class="obbligazioni-table__mobile-card-item">
                                            <span class="obbligazioni-table__mobile-card-label">Codice Serie:</span>
                                            <span class="obbligazioni-table__mobile-card-value">{row.codSerie}</span>
                                          </div>
                                          <div class="obbligazioni-table__mobile-card-item">
                                            <span class="obbligazioni-table__mobile-card-label">Emesso dal:</span>
                                            <span class="obbligazioni-table__mobile-card-value">{row.emessoDal}</span>
                                          </div>
                                          <div class="obbligazioni-table__mobile-card-item">
                                            <span class="obbligazioni-table__mobile-card-label">Emesso Al:</span>
                                            <span class="obbligazioni-table__mobile-card-value bold">{row.emessoAl}</span>
                                          </div>
                                          <div class="obbligazioni-table__mobile-card-button">
                                            <a
                                                class="btn btn-primary"
                                                key={`${row.codSerie}-${row.emessoDal}-${idx}`}
                                                onClick={() => !loadingDetail && handleRowClick(row)}
                                                style={{ cursor: loadingDetail ? 'wait' : 'pointer' }}
                                            >Dettaglio</a>
                                          </div>
                                        </div>
                                    ))}
                                  </div>
                                </div>
                              </section>
                            </>
                        )
                    )}
                  </>
              )}

              {/* DETAIL VIEW */}
              {view === 'detail' && (
                  <>
                    {loadingDetail ? <LoadingSpinner /> : renderDetailView()}
                  </>
              )}

            </div>
          </div>
        </div>
      </section>
  );
}

export default App;